export const TRADE_STAGES = [
  'load',
  'sail',
  'unload',
  'deliver',
  'lift',
  'weigh',
  'store',
  'return-cart',
  'return-boat',
] as const;
export type TradeStage = (typeof TRADE_STAGES)[number];
export type CargoOwner = 'sea' | 'lighter' | 'cart' | 'scale' | 'stored';

export interface TradeTimetable {
  readonly starts: Readonly<Record<TradeStage, number>>;
  readonly durations: Readonly<Record<TradeStage, number>>;
  readonly water: number;
  readonly cart: number;
  readonly arrival: number;
  readonly cycle: number;
}

export function tradeTimetable(water: number, cart: number, arrival: number): TradeTimetable {
  if (![water, cart, arrival].every((v) => Number.isFinite(v) && v > 0) || arrival >= cart)
    throw new Error(
      'Harbour trade requires positive travel lengths and a delivery before the cart returns'
    );
  const durations = {
    load: 20,
    sail: water / 1.5 + 4,
    unload: 34,
    deliver: arrival / 0.85 + 4,
    lift: 12,
    weigh: 16,
    store: 10,
    'return-cart': (cart - arrival) / 0.85 + 4,
    'return-boat': water / 1.5 + 4,
  };
  const starts = {} as Record<TradeStage, number>;
  let cycle = 0;
  for (const stage of TRADE_STAGES) {
    starts[stage] = cycle;
    cycle += durations[stage];
  }
  return { starts, durations, cycle, water, cart, arrival };
}

export function tradeEase(t: number): number {
  const q = Math.max(0, Math.min(1, t));
  return q * q * (3 - 2 * q);
}

/** Acceleration and braking integrate the same smooth speed curve, keeping footfall distance honest. */
function travel(length: number, speed: number, time: number) {
  const ramp = 4,
    duration = length / speed + ramp;
  const acceleration = (t: number) => {
    const q = Math.max(0, Math.min(1, t / ramp));
    return speed * ramp * (q ** 3 - 0.5 * q ** 4);
  };
  if (time <= ramp) return { distance: acceleration(time), speed: speed * tradeEase(time / ramp) };
  if (time >= duration - ramp)
    return {
      distance: length - acceleration(duration - time),
      speed: speed * tradeEase((duration - time) / ramp),
    };
  return { distance: speed * (time - ramp / 2), speed };
}

/** One lot has one owner throughout the voyage; the cart and lighter return empty. */
export function tradeMotionAt(plan: TradeTimetable, seconds: number) {
  const cycles = Math.floor(seconds / plan.cycle);
  const time = ((seconds % plan.cycle) + plan.cycle) % plan.cycle;
  const stage =
    TRADE_STAGES.find((s) => time < plan.starts[s] + plan.durations[s]) ?? 'return-boat';
  const elapsed = time - plan.starts[stage],
    progress = elapsed / plan.durations[stage];
  const sailed = stage === 'sail' ? travel(plan.water, 1.5, elapsed) : null;
  const returning = stage === 'return-boat' ? travel(plan.water, 1.5, elapsed) : null;
  const delivery = stage === 'deliver' ? travel(plan.arrival, 0.85, elapsed) : null;
  const cartBack = stage === 'return-cart' ? travel(plan.cart - plan.arrival, 0.85, elapsed) : null;
  const boatDistance =
    stage === 'load'
      ? 0
      : sailed
        ? sailed.distance
        : returning
          ? plan.water - returning.distance
          : plan.water;
  const cartDistance =
    cycles * plan.cart +
    (delivery
      ? delivery.distance
      : cartBack
        ? plan.arrival + cartBack.distance
        : time >= plan.starts['return-boat']
          ? plan.cart
          : time >= plan.starts.lift
            ? plan.arrival
            : 0);
  const owner: CargoOwner =
    stage === 'load'
      ? 'sea'
      : stage === 'sail' || stage === 'unload'
        ? 'lighter'
        : stage === 'deliver' || stage === 'lift'
          ? 'cart'
          : stage === 'weigh' || stage === 'store'
            ? 'scale'
            : 'stored';
  const boatTurn =
    stage === 'load'
      ? Math.PI * (1 - tradeEase(progress))
      : stage === 'sail'
        ? 0
        : stage === 'unload'
          ? Math.PI * tradeEase((elapsed - 10) / 14)
          : Math.PI;
  return {
    stage,
    owner,
    progress,
    boatDistance,
    cartDistance,
    boatSpeed: sailed?.speed ?? returning?.speed ?? 0,
    cartSpeed: delivery?.speed ?? cartBack?.speed ?? 0,
    boatTurn,
  };
}
